import { useQuery } from '@provision/graphql-client';
import { GET_NOTIFICATION_META_QUERY } from 'src/graphql/entities/notification/notification.operations';
import { NotificationStatus } from 'src/graphql/entities/notification/notification.types';
import {
  buildNotificationCompositeKey,
  getBaseNotificationFilters,
} from 'src/graphql/entities/notification/utils';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { QueryVariables, Records } from 'src/graphql/types';
import { MAX_NOTIFICATIONS_LIMIT } from './use-notification-records';
import { useNotificationCountSubscription } from './use-notification-subscription';

export type GetNotificationCount = {
  getNotification: Records<{
    id: string;
    entityId: string | number;
    entity: string;
    __typename: string;
  }>;
};

export const useNotificationCount = () => {
  const { user } = useCurrentUserProfile();
  const { data, client, variables } = useQuery<GetNotificationCount, QueryVariables>(
    GET_NOTIFICATION_META_QUERY,
    {
      variables: {
        params: getNotificationCountParams(user?.id!),
      },
      // refetch every 30 mins to reconcile possible edge cases
      pollInterval: 18_000_00,
      skip: !user?.id,
    },
  );

  useNotificationCountSubscription(client, variables!);

  const unreadUniqueEntityCount = (() => {
    const recs = data?.getNotification?.records || [];
    const unique = new Set(
      recs.map((r) => buildNotificationCompositeKey(r.entity, r.entityId)),
    );
    return unique.size;
  })();

  return {
    unreadCount: unreadUniqueEntityCount,
  };
};

export function getNotificationCountParams(userId: string): QueryVariables['params'] {
  return {
    filters: [
      ...getBaseNotificationFilters(userId),
      { attribute: 'status', operator: '=', value: NotificationStatus.Unread },
    ],
    sorting: { attribute: 'updatedAt', desc: true },
    limit: MAX_NOTIFICATIONS_LIMIT,
  };
}
